<template>
  <div>
    <ToastNotification
      :showToast="isToastVisible"
      :type="isToastType"
      :message="toastMessage"
    />

    <NavigationLayout
      :navigationBarStyle="{
        'top': 'var(--safe-area-inset-top, 0px)',
      }"
      :navigationContentStyle="{
        'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
      }"
    >
      <div class="upload-container" slot="contents">
        <h2 class="page-title">{{ $t('live2DModelUpload') }}</h2>
        <div class="upload-section">
          <p class="upload-description">
            {{ $t('live2DModelUploadModel') }}
          </p>
          <div
            class="upload-area"
            :class="{ 'drag-over': isDragOver }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".zip"
              style="display: none"
              @change="handleFileSelect"
            />
            <span class="upload-icon">⬆️</span>
            <div class="upload-text">
              {{ $t('live2DDrugAndDropFile') }}
            </div>
            <div class="upload-or">{{ $t('live2DOr') }}</div>
            <button class="file-select-button" type="button">
              {{ $t('live2DFileSelect') }}
            </button>
          </div>

          <div v-if="selectedFile || isUploading">
            <div class="progress-wrapper">
              <div class="icon-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="file-icon"
                >
                  <path
                    d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
                  ></path>
                  <polyline points="13 2 13 9 20 9"></polyline>
                </svg>
              </div>

              <div class="content-wrapper">
                <span class="file-name">{{
                  selectedFile ? selectedFile.name : 'model.zip'
                }}</span>
                <template v-if="!isComplete">
                  <div class="progress-container" v-if="isUploading">
                    <div
                      class="progress-bar"
                      :style="{ width: uploadProgress + '%' }"
                    ></div>
                  </div>
                  <div class="status-info">
                    <span class="file-size">{{ fileSize }}MB</span>
                    <div class="status-text" v-if="isUploading">
                      <span class="percentage">{{ uploadProgress }}%</span>
                    </div>
                  </div>
                </template>

                <div v-else class="complete-status">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="checkmark-icon"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>{{ $t('live2DUploadComplete') }}</span>
                </div>
              </div>

              <div v-if="!isComplete">
                <button class="close-button" @click="clearSelectedFile">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="close-icon"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <ul class="upload-requirements">
            <li>{{ $t('live2DUploadZipOnly') }}</li>
            <li>{{ $t('live2DUploadLimitSize', { 0: 50 }) }}</li>
            <li>{{ $t('live2DUploadFileNameNotice') }}</li>
          </ul>

          <button
            class="upload-submit-btn"
            :disabled="!selectedFile || isUploading"
            @click="uploadModel"
          >
            {{ $t('live2DUpload') }}
          </button>
        </div>

        <div class="upload-status--section">
          <div class="guide-title">{{ $t('live2DModelUploadStatus') }}</div>
          <ul class="guide-list" v-if="!hasAlreadyUploadFile">
            <li>
              {{ $t('live2DModelUploadStatusNotFound') }}
            </li>
          </ul>
          <ul class="guide-list" v-if="hasAlreadyUploadFile">
            <li>
              {{ $t('live2DModelUploadStatusUploadDate') }}:
              {{ getUploadStartDateString() }}
            </li>
            <li>
              {{ $t('live2DModelUploadStatusLatestDate') }}:
              {{ getUploadUpdateDateString() }}
            </li>
            <li>
              {{ $t('live2DModelUploadStatusUploadState') }}:
              {{ getUploadStatusString() }}
            </li>
          </ul>
        </div>

        <div class="guide-section">
          <div class="guide-title">{{ $t('live2DUploadNotice') }}</div>
          <ul class="guide-list">
            <li>{{ $t('live2DUploadUseOnlyStream') }}</li>
            <li>{{ $t('live2DUploadAlert') }}</li>
          </ul>
        </div>
      </div>
    </NavigationLayout>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import NavigationLayout from '@/components/NavigationLayout.vue'
import ToastNotification from '@/components/ToastNotification.vue'
import Component from 'vue-class-component'
import { checkInMaintenance } from '@/utils/maintenance'
import {
  handleUnauthorized,
  showGenericNetworkError,
} from '@/utils/handle_error'
import { uploadLive2DModel, UploadProgressEvent } from '@/utils/fileUpload'
import { apiV2 } from '@/api/appGatewayServerV2Api'
import i18n from '@/i18n'
import dayjs from 'dayjs'
import { UploadStatus } from '@/api/pb/Live2d/Common/UploadStatus'

@Component({
  components: {
    NavigationLayout,
    ToastNotification,
  },
})
export default class Live2dModelUploadPage extends Vue {
  selectedFile: File | null = null
  isDragOver = false
  isUploading = false
  uploadProgress = 0
  isComplete = false
  isToastVisible = false
  isToastType = 'info' // 'info' または 'error'
  toastMessage = ''

  hasAlreadyUploadFile = false
  uploadStatus = 0
  uploadStartDate = new Date()
  uploadUpdateDate = new Date()

  async created(): Promise<void> {
    try {
      await this.initialize()
      await this.getUploadLive2DModelStatus()
    } catch (e) {
      if (checkInMaintenance(e)) {
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {})
        return
      }
      handleUnauthorized(e)
    }
  }

  async initialize(): Promise<void> {
    // Live2D利用権限チェック
    const response = await apiV2().live2d.canUse()
    if (!response.canUseLive2d) {
      this.$router.push({ path: '/mypage' }).catch(() => {})
    }
  }

  handleDragOver(e: DragEvent): void {
    this.isDragOver = true
  }

  handleDragLeave(e: DragEvent): void {
    this.isDragOver = false
  }

  handleDrop(e: DragEvent): void {
    this.isDragOver = false
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      this.selectFile(files[0])
    }
  }

  triggerFileInput(): void {
    this.clearSelectedFile()
    const fileInput = this.$refs.fileInput as HTMLInputElement
    fileInput.click()
  }

  handleFileSelect(e: Event): void {
    const target = e.target as HTMLInputElement
    const files = target.files
    if (files && files.length > 0) {
      this.selectFile(files[0])
    }
  }

  selectFile(file: File): void {
    // ファイルサイズチェック（50MB以下）
    const maxSize = 50 * 1024 * 1024
    if (file.size > maxSize) {
      this.showAlertToast(i18n.t('live2DUploadLimitSize', { 0: 50 }).toString())
      return
    }

    // ファイル形式チェック
    if (!file.name.toLowerCase().endsWith('.zip')) {
      this.showAlertToast(i18n.t('live2DUploadComplete').toString())
      return
    }

    // ファイル名チェック（半角英数のみ）
    const fileName = file.name.replace('.zip', '')
    if (!/^[a-zA-Z0-9._-]+$/.test(fileName)) {
      this.showAlertToast(i18n.t('live2DUploadOnlyFileNameType').toString())
      return
    }
    this.selectedFile = file
  }

  get fileSize(): string {
    if (!this.selectedFile) return '0.00'
    return (this.selectedFile.size / (1024 * 1024)).toFixed(2)
  }

  clearSelectedFile(): void {
    this.selectedFile = null
    this.isUploading = false
    this.uploadProgress = 0
    const fileInput = this.$refs.fileInput as HTMLInputElement
    fileInput.value = ''
    this.isComplete = false
  }

  async uploadModel(): Promise<void> {
    if (!this.selectedFile) return

    this.isUploading = true
    this.uploadProgress = 0

    try {
      const response = await apiV2().live2d.model.uploadUrl()
      const uploadUrl = response.uploadUrl
      if (response.uploadUrl === '') {
        this.showAlertToast(i18n.t('live2DUploadFailed').toString())
        return
      }

      // XHRベースのアップロード（リアルタイムプログレス表示）
      await uploadLive2DModel(
        uploadUrl,
        this.selectedFile,
        (progress: UploadProgressEvent) => {
          this.uploadProgress = progress.percentage
        }
      )
      this.showInfoToast(i18n.t('live2DUploadSuccess').toString())
      this.handleUploadComplete()
    } catch (error) {
      this.isUploading = false
      this.uploadProgress = 0

      // エラーメッセージの表示
      if (error instanceof Error) {
        this.showAlertToast(i18n.t('live2DUploadFailed').toString())
      } else {
        showGenericNetworkError()
      }
    }
  }

  async getUploadLive2DModelStatus(): Promise<void> {
    try {
      const response = await apiV2().live2d.model.uploadStatus()
      if (
        response.createdAt !== undefined &&
        response.updatedAt !== undefined
      ) {
        this.hasAlreadyUploadFile = true
        this.uploadStatus = response.status
        this.uploadStartDate = response.createdAt
        this.uploadUpdateDate = response.updatedAt
      }
    } catch (error) {
      this.showAlertToast(i18n.t('live2DModelUploadStatusGetFailed').toString())
    }
  }

  getUploadStartDateString(): string {
    if (!this.hasAlreadyUploadFile) return ''
    return dayjs(this.uploadStartDate)
      .tz('Asia/Tokyo')
      .format('YYYY/MM/DD HH:mm:ss')
  }

  getUploadUpdateDateString(): string {
    if (!this.hasAlreadyUploadFile) return ''
    return dayjs(this.uploadUpdateDate)
      .tz('Asia/Tokyo')
      .format('YYYY/MM/DD HH:mm:ss')
  }

  getUploadStatusString(): string {
    if (!this.hasAlreadyUploadFile) return ''
    switch (this.uploadStatus) {
      case UploadStatus.UPLOAD_STATUS_UNKNOWN:
        return i18n.t('live2DModelUploadStatusUnknown').toString()
      case UploadStatus.UPLOAD_STATUS_START:
        return i18n.t('live2DModelUploadStatusStart').toString()
      case UploadStatus.UPLOAD_STATUS_COMPLETE:
        return i18n.t('live2DModelUploadStatusComplete').toString()
      case UploadStatus.UPLOAD_STATUS_FILE_FORMAT_ERROR:
        return i18n.t('live2DModelUploadStatusFileFormatError').toString()
      case UploadStatus.UPLOAD_STATUS_OTHER_ERROR:
        return i18n.t('live2DModelUploadStatusOtherError').toString()
    }
    return i18n.t('live2DModelUploadStatusUnknown').toString()
  }

  handleUploadComplete(): void {
    // 再アップさせないためにisUploadingフラグは落としたまま
    this.uploadProgress = 100
    // アップロード完了後、選択ファイルをクリア
    setTimeout(() => {
      this.isComplete = true
      this.getUploadLive2DModelStatus()
    }, 2000)
  }

  showAlertToast(message: string): void {
    this.isToastType = 'error'
    this.toastMessage = message
    this.isToastVisible = true
    setTimeout(() => {
      this.closeToast()
    }, 2000)
  }

  showInfoToast(message: string): void {
    this.isToastType = 'info'
    this.toastMessage = message
    this.isToastVisible = true
    setTimeout(() => {
      this.closeToast()
    }, 2000)
  }

  closeToast(): void {
    this.isToastVisible = false
    this.toastMessage = ''
  }
}
</script>

<style scoped>
.upload-container {
  max-width: 500px;
  margin: 40px auto;
  padding: 24px 16px;
  font-family: sans-serif;
}

@media (min-width: 800px) {
  .upload-container {
    width: 500px;
  }
}

.page-title {
  margin-bottom: 18px;
  color: #0f0f0f;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.2em;
}

.upload-section {
  padding: 28px 20px 22px;
  margin-bottom: 22px;
  background: #f7fafd;
  border-radius: 18px;
}

.upload-description {
  margin-bottom: 14px;
  color: #0f0f0f;
  font-size: 14px;
  font-weight: bold;
}

.upload-area {
  padding: 34px 0;
  margin-bottom: 18px;
  color: #5a8dfc;
  font-size: 14px;
  text-align: center;
  background: #eaf4fe;
  border: 2px dashed #71a8f7;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-area:hover,
.upload-area.drag-over {
  background: #d6ebfd;
  border-color: #428dfa;
}

.upload-icon {
  display: block;
  margin-bottom: 8px;
  font-size: 32px;
}

.upload-text {
  margin-top: 8px;
  color: #5a8dfc;
  font-size: 14px;
}

.upload-or {
  margin: 10px 0;
  color: #666;
  font-size: 14px;
}

.file-select-button {
  padding: 0.55rem 2rem;
  font-size: 1.1rem;
  font-weight: bold;
  color: #3b82f6;
  background-color: #eaf4ff;
  border: 2px solid #3b82f6;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.file-select-button:hover {
  background-color: #dbe9fe;
}

.upload-requirements {
  margin-left: 12px;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  list-style: disc;
}

.upload-status--section {
  padding: 16px 16px 10px;
  margin-bottom: 20px;
  background: #e5e7eb;
  border-radius: 14px;
}

.guide-section {
  padding: 16px 16px 10px;
  margin-bottom: 20px;
  background: #fff0e5;
  border-radius: 14px;
}

.guide-title {
  margin-bottom: 8px;
  color: #0f0f0f;
  font-size: 14px;
  font-weight: bold;
}

.guide-list {
  margin: 8px 0 0 18px;
  font-size: 13px;
  color: #0f0f0f;
  line-height: 1.6;
  list-style: disc;
}

.upload-submit-btn {
  width: 100%;
  padding: 14px 0;
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  background: #428dfa;
  border: none;
  border-radius: 22px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.upload-submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.upload-submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem;
  margin-top: 20px;
  margin-bottom: 20px;
  overflow: hidden;
  font-family: sans-serif;
  background-color: #f7fafd;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgb(0 0 0 / 5%);
}

.icon-wrapper,
.action-wrapper {
  flex-shrink: 0;
}

.icon-wrapper .file-icon {
  color: #6b7280;
}

.content-wrapper {
  flex-grow: 1;
  min-width: 0;
}

.file-name {
  display: block;
  overflow: hidden;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.progress-container {
  width: 100%;
  height: 6px;
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
  overflow: hidden;
  background-color: #e5e7eb;
  border-radius: 9999px;
}

.progress-bar {
  height: 100%;
  background-color: #3b82f6;
  border-radius: 9999px;
  transition: width 0.1s linear;
}

.status-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #6b7280;
}

.close-button {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  line-height: 1;
  background: transparent;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
}

.close-button .close-icon {
  color: #6b7280;
  transition: color 0.2s;
}

.close-button:hover .close-icon {
  color: #111827;
}

.complete-status {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 25px;
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.complete-status span {
  font-size: 0.9rem;
  font-weight: 600;
  color: #3b82f6;
}

.checkmark-icon {
  color: #3b82f6;
}
</style>
